import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendar, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const LumpsumCalculator = () => {
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [years, setYears] = useState('');
  const [compounding, setCompounding] = useState('yearly');
  const [result, setResult] = useState('0.00');
  const [error, setError] = useState('');

  const calculateLumpsum = () => {
    if (!principal || !rate || !years || !compounding) {
      setError('Please fill in all fields');
      return;
    }

    const p = parseFloat(principal);
    const r = parseFloat(rate) / 100;
    const t = parseFloat(years);
    let n = 1;

    if (isNaN(p) || isNaN(r) || isNaN(t)) {
      setError('Please enter valid numbers');
      return;
    }

    switch (compounding) {
      case 'monthly':
        n = 12;
        break;
      case 'quarterly':
        n = 4;
        break;
      case 'half-yearly':
        n = 2;
        break;
      case 'yearly':
        n = 1;
        break;
    }

    const amount = p * Math.pow(1 + (r / n), n * t);
    setResult(amount.toFixed(2));
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getChartData = () => {
    const p = parseFloat(principal) || 0;
    const r = parseFloat(rate) / 100 || 0;
    const t = parseFloat(years) || 0;
    let n = 1;

    switch (compounding) {
      case 'monthly':
        n = 12;
        break;
      case 'quarterly':
        n = 4;
        break;
      case 'half-yearly':
        n = 2;
        break;
      case 'yearly':
        n = 1;
        break;
    }

    const data = [];
    for (let i = 0; i <= t; i++) {
      const amount = p * Math.pow(1 + (r / n), n * i);
      data.push({
        year: i,
        amount: amount
      });
    }

    return data;
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-100 min-h-screen p-8">
      <Helmet>
        <title>Lumpsum Calculator</title>
        <meta name="description" content="Calculate the future value of your lumpsum investment with different compounding periods." />
        <meta property="og:title" content="Lumpsum Calculator" />
        <meta property="og:description" content="Calculate the future value of your lumpsum investment with different compounding periods." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaChartLine className="mr-3 text-4xl text-indigo-600" />
            Lumpsum Calculator
          </h2>
          <button 
            className="group relative text-indigo-600 hover:text-indigo-700 transition-colors duration-300"
            title="Help"
          >
            <FaInfoCircle className="w-7 h-7" />
            <span className="absolute -top-1 -right-1 bg-indigo-500 text-white px-2 py-1 text-xs rounded-full group-hover:scale-110 transition-transform duration-300">
              ?
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaRupeeSign className="inline-block mr-2 text-indigo-500" />
              Principal Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
              <input
                type="number"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
                placeholder="Enter principal amount"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaChartLine className="inline-block mr-2 text-indigo-500" />
              Annual Interest Rate (%)
            </label>
            <input
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
              placeholder="Enter interest rate"
              min="0"
              step="0.01"
            />
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaCalendar className="inline-block mr-2 text-indigo-500" />
              Investment Period (Years)
            </label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
              placeholder="Enter number of years"
              min="0"
            />
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaChartLine className="inline-block mr-2 text-indigo-500" />
              Compounding Frequency
            </label>
            <select
              value={compounding}
              onChange={(e) => setCompounding(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
            >
              <option value="yearly">Yearly</option>
              <option value="half-yearly">Half-Yearly</option>
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculateLumpsum}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-red-600 font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10"
        >
          <FaChartLine className="inline-block mr-2" />
          Calculate Investment
        </button>

        <div className="space-y-8">
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-indigo-600" />
              Investment Details
            </h2>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
                {error}
              </div>
            )}
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Principal Amount</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(parseFloat(principal))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Interest Rate</span>
                <span className="font-semibold text-gray-600">
                  {rate}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Investment Period</span>
                <span className="font-semibold text-gray-600">
                  {years} years
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Compounding Frequency</span>
                <span className="font-semibold text-gray-600">
                  {compounding.charAt(0).toUpperCase() + compounding.slice(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Maturity Amount</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(parseFloat(result))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Interest Earned</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(parseFloat(result) - parseFloat(principal))}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-indigo-600" />
              Investment Growth
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" tickFormatter={(value) => `${value} year${value !== 1 ? 's' : ''}`} />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="amount" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaInfoCircle className="mr-3 text-indigo-600" />
              How to Use
            </h2>
            <ol className="list-decimal list-inside space-y-6">
              <li className="flex items-start">
                <span className="font-semibold mr-2">1. Enter Principal Amount:</span>
                <span className="text-gray-600">Input the amount you want to invest</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">2. Enter Interest Rate:</span>
                <span className="text-gray-600">Input the annual interest rate (in %)</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">3. Enter Investment Period:</span>
                <span className="text-gray-600">Input the number of years for investment</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">4. Select Compounding Frequency:</span>
                <span className="text-gray-600">Choose how often the interest is compounded</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">5. Calculate:</span>
                <span className="text-gray-600">Click the Calculate button to see your investment growth</span>
              </li>
            </ol>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-indigo-600" />
              Benefits of Lumpsum Investment
            </h2>
            <ul className="list-disc list-inside space-y-4">
              <li className="flex items-start">
                <span className="font-semibold mr-2">Compound Interest:</span>
                <span className="text-gray-600">Your money grows exponentially over time</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">Flexibility:</span>
                <span className="text-gray-600">Choose different compounding frequencies</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">Visual Growth:</span>
                <span className="text-gray-600">See how your investment grows over time</span>
              </li>
              <li className="flex items-start">
                <span className="font-semibold mr-2">Long-term Planning:</span>
                <span className="text-gray-600">Plan for your financial goals effectively</span>
              </li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaInfoCircle className="mr-3 text-indigo-600" />
              Key Features
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-indigo-50 rounded-xl">
                <div className="flex items-center mb-3">
                  <FaRupeeSign className="text-indigo-500 text-2xl mr-3" />
                  <h3 className="font-semibold text-indigo-600">Flexible Investment Amount</h3>
                </div>
                <p className="text-gray-600">Invest any amount starting from ₹1,000</p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-xl">
                <div className="flex items-center mb-3">
                  <FaChartLine className="text-indigo-500 text-2xl mr-3" />
                  <h3 className="font-semibold text-indigo-600">Multiple Compounding Options</h3>
                </div>
                <p className="text-gray-600">Choose from yearly, half-yearly, quarterly, or monthly compounding</p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-xl">
                <div className="flex items-center mb-3">
                  <FaCalendar className="text-indigo-500 text-2xl mr-3" />
                  <h3 className="font-semibold text-indigo-600">Custom Investment Period</h3>
                </div>
                <p className="text-gray-600">Plan for any duration from 1 to 50 years</p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-xl">
                <div className="flex items-center mb-3">
                  <FaInfoCircle className="text-indigo-500 text-2xl mr-3" />
                  <h3 className="font-semibold text-indigo-600">Detailed Insights</h3>
                </div>
                <p className="text-gray-600">View total interest earned and investment growth over time</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaQuestionCircle className="mr-3 text-indigo-600" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-indigo-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3">What is lumpsum investment?</h3>
                <p className="text-gray-600">Lumpsum investment is a one-time investment of a fixed amount of money in a financial instrument or scheme.</p>
              </div>
              <div className="bg-indigo-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3">How does compounding work?</h3>
                <p className="text-gray-600">Compounding is the process where interest earned on your investment is reinvested to generate additional earnings over time.</p>
              </div>
              <div className="bg-indigo-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3">What is the minimum investment period?</h3>
                <p className="text-gray-600">The minimum investment period is 1 year, but you can choose any duration up to 50 years.</p>
              </div>
              <div className="bg-indigo-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3">How accurate are the calculations?</h3>
                <p className="text-gray-600">Our calculations are based on standard financial formulas and provide accurate estimates for planning purposes.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LumpsumCalculator;
